import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Tabs,
  Tab,
  Alert,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  CircularProgress,
  Card,
  CardContent,
  CardActions,
  Chip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Pagination,
} from '@mui/material';
import {
  BugReport as BugIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import BugService, { Bug, BugStatistics, CreateBugRequest } from '../../services/BugService';
import { DeveloperDashboardService } from '../../services/DeveloperDashboardService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`bugs-tabpanel-${index}`}
      aria-labelledby={`bugs-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

/**
 * Developer Bug Tracking Page - VERSION 2.0
 * Full API integration with CRUD operations
 */
const DeveloperBugsPage: React.FC = () => {
  console.log('🐛 DeveloperBugsPage MOUNTED - VERSION 2.0 (API INTEGRATED) 🐛');
  
  const [currentTab, setCurrentTab] = useState(0);
  const [bugs, setBugs] = useState<Bug[]>([]);
  const [myBugs, setMyBugs] = useState<Bug[]>([]);
  const [statistics, setStatistics] = useState<BugStatistics | null>(null);
  const [projects, setProjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Filters
  const [filterProject, setFilterProject] = useState<number | ''>('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterSeverity, setFilterSeverity] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Create/Edit bug dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBug, setEditingBug] = useState<Bug | null>(null);
  const [formData, setFormData] = useState<Partial<CreateBugRequest>>({
    title: '',
    description: '',
    project_id: 0,
    priority: 'normal',
    status: 'todo',
    steps_to_reproduce: '',
    expected_behavior: '',
    actual_behavior: '',
    environment: '',
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (currentTab === 0) {
      loadBugs();
    } else if (currentTab === 1) {
      loadMyBugs();
    }
  }, [currentTab, page, filterProject, filterStatus, filterSeverity, searchTerm]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [statsData, projectsData] = await Promise.all([
        BugService.getStatistics(),
        DeveloperDashboardService.getDashboardData(),
      ]);
      setStatistics(statsData);
      setProjects(projectsData.projects);
    } catch (err) {
      console.error('Error loading initial data:', err);
      setError('Failed to load bug tracking data');
    } finally {
      setLoading(false);
    }
  };

  const loadBugs = async () => {
    try {
      setLoading(true);
      const filters = {
        page,
        per_page: 10,
        ...(filterProject && { project_id: filterProject as number }),
        ...(filterStatus && { status: filterStatus }),
        ...(filterSeverity && { severity: filterSeverity }),
        ...(searchTerm && { search: searchTerm }),
      };
      const response = await BugService.getBugs(filters);
      setBugs(response.data);
      setTotalPages(response.meta?.last_page || 1);
    } catch (err) {
      console.error('Error loading bugs:', err);
      setError('Failed to load bugs');
    } finally {
      setLoading(false);
    }
  };

  const loadMyBugs = async () => {
    try {
      setLoading(true);
      const response = await BugService.getMyBugs({ page, per_page: 10 });
      setMyBugs(response.data);
      setTotalPages(response.meta?.last_page || 1);
    } catch (err) {
      console.error('Error loading my bugs:', err);
      setError('Failed to load your bugs');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
    setPage(1);
  };

  const handleOpenDialog = (bug?: Bug) => {
    if (bug) {
      setEditingBug(bug);
      setFormData({
        title: bug.title,
        description: bug.description,
        project_id: bug.project_id,
        priority: bug.priority,
        status: bug.status,
        ...(bug.metadata && {
          steps_to_reproduce: bug.metadata.steps_to_reproduce || '',
          expected_behavior: bug.metadata.expected_behavior || '',
          actual_behavior: bug.metadata.actual_behavior || '',
          environment: bug.metadata.environment || '',
        }),
      });
    } else {
      setEditingBug(null);
      setFormData({
        title: '',
        description: '',
        project_id: projects[0]?.id || 0,
        priority: 'normal',
        status: 'todo',
        steps_to_reproduce: '',
        expected_behavior: '',
        actual_behavior: '',
        environment: '',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingBug(null);
  };

  const handleSubmit = async () => {
    try {
      if (editingBug) {
        await BugService.updateBug(editingBug.id, formData);
      } else {
        await BugService.createBug(formData as CreateBugRequest);
      }
      handleCloseDialog();
      await Promise.all([loadBugs(), loadMyBugs(), loadInitialData()]);
    } catch (err) {
      console.error('Error saving bug:', err);
      setError('Failed to save bug');
    }
  };

  const handleDelete = async (bugId: number) => {
    if (window.confirm('Are you sure you want to delete this bug?')) {
      try {
        await BugService.deleteBug(bugId);
        await Promise.all([loadBugs(), loadMyBugs(), loadInitialData()]);
      } catch (err) {
        console.error('Error deleting bug:', err);
        setError('Failed to delete bug');
      }
    }
  };

  const handleStatusChange = async (bugId: number, newStatus: Bug['status']) => {
    try {
      await BugService.updateBugStatus(bugId, newStatus);
      await Promise.all([loadBugs(), loadMyBugs(), loadInitialData()]);
    } catch (err) {
      console.error('Error updating status:', err);
      setError('Failed to update status');
    }
  };

  const renderBugTable = (bugList: Bug[]) => (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>ID</TableCell>
            <TableCell>Title</TableCell>
            <TableCell>Project</TableCell>
            <TableCell>Priority</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Assigned To</TableCell>
            <TableCell>Created</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {bugList.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} align="center">
                <Typography color="text.secondary">No bugs found</Typography>
              </TableCell>
            </TableRow>
          ) : (
            bugList.map((bug) => (
              <TableRow key={bug.id}>
                <TableCell>{bug.id}</TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {bug.title}
                  </Typography>
                </TableCell>
                <TableCell>{bug.project?.name || 'N/A'}</TableCell>
                <TableCell>
                  <Chip
                    label={bug.priority}
                    size="small"
                    color={BugService.getSeverityColor(bug.priority)}
                  />
                </TableCell>
                <TableCell>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <Select
                      value={bug.status}
                      onChange={(e) => handleStatusChange(bug.id, e.target.value as Bug['status'])}
                    >
                      <MenuItem value="todo">To Do</MenuItem>
                      <MenuItem value="in_progress">In Progress</MenuItem>
                      <MenuItem value="in_review">In Review</MenuItem>
                      <MenuItem value="testing">Testing</MenuItem>
                      <MenuItem value="done">Done</MenuItem>
                      <MenuItem value="cancelled">Cancelled</MenuItem>
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell>{bug.assignedUser?.name || 'Unassigned'}</TableCell>
                <TableCell>{new Date(bug.created_at).toLocaleDateString()}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1}>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => handleOpenDialog(bug)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => handleDelete(bug.id)} color="error">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            <BugIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Bug Tracking
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Track, report, and resolve bugs in your projects
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadInitialData}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Report Bug
          </Button>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Statistics Cards */}
      {statistics && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" variant="body2">Total Bugs</Typography>
                <Typography variant="h4">{statistics.total}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" variant="body2">Open</Typography>
                <Typography variant="h4" color="warning.main">{statistics.open}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" variant="body2">In Progress</Typography>
                <Typography variant="h4" color="primary.main">{statistics.in_progress}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" variant="body2">Resolved</Typography>
                <Typography variant="h4" color="success.main">{statistics.resolved}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tabs */}
      <Paper sx={{ mt: 3 }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          aria-label="bug tracking tabs"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="All Bugs" />
          <Tab label="My Bugs" />
        </Tabs>

        {/* All Bugs Tab */}
        <TabPanel value={currentTab} index={0}>
          {/* Filters */}
          <Box sx={{ mb: 3 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Project</InputLabel>
                  <Select
                    value={filterProject}
                    label="Project"
                    onChange={(e) => setFilterProject(e.target.value as number | '')}
                  >
                    <MenuItem value="">All Projects</MenuItem>
                    {projects.map(p => (
                      <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filterStatus}
                    label="Status"
                    onChange={(e) => setFilterStatus(e.target.value)}
                  >
                    <MenuItem value="">All Statuses</MenuItem>
                    <MenuItem value="todo">To Do</MenuItem>
                    <MenuItem value="in_progress">In Progress</MenuItem>
                    <MenuItem value="testing">Testing</MenuItem>
                    <MenuItem value="done">Done</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Priority</InputLabel>
                  <Select
                    value={filterSeverity}
                    label="Priority"
                    onChange={(e) => setFilterSeverity(e.target.value)}
                  >
                    <MenuItem value="">All Priorities</MenuItem>
                    <MenuItem value="urgent">Urgent</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                    <MenuItem value="normal">Normal</MenuItem>
                    <MenuItem value="low">Low</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Search"
                  placeholder="Search bugs..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </Grid>
            </Grid>
          </Box>

          {loading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {renderBugTable(bugs)}
              {totalPages > 1 && (
                <Box display="flex" justifyContent="center" mt={2}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(_, value) => setPage(value)}
                  />
                </Box>
              )}
            </>
          )}
        </TabPanel>

        {/* My Bugs Tab */}
        <TabPanel value={currentTab} index={1}>
          {loading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {renderBugTable(myBugs)}
              {totalPages > 1 && (
                <Box display="flex" justifyContent="center" mt={2}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(_, value) => setPage(value)}
                  />
                </Box>
              )}
            </>
          )}
        </TabPanel>
      </Paper>

      {/* Create/Edit Bug Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingBug ? 'Edit Bug' : 'Report New Bug'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Project *</InputLabel>
                <Select
                  value={formData.project_id}
                  label="Project *"
                  onChange={(e) => setFormData({ ...formData, project_id: e.target.value as number })}
                >
                  {projects.map(p => (
                    <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Priority *</InputLabel>
                <Select
                  value={formData.priority}
                  label="Priority *"
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value as any })}
                >
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="normal">Normal</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="urgent">Urgent</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Bug Title *"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Description *"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Steps to Reproduce"
                value={formData.steps_to_reproduce}
                onChange={(e) => setFormData({ ...formData, steps_to_reproduce: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Expected Behavior"
                value={formData.expected_behavior}
                onChange={(e) => setFormData({ ...formData, expected_behavior: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Actual Behavior"
                value={formData.actual_behavior}
                onChange={(e) => setFormData({ ...formData, actual_behavior: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Environment (Browser, OS, etc.)"
                value={formData.environment}
                onChange={(e) => setFormData({ ...formData, environment: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            disabled={!formData.title || !formData.description || !formData.project_id}
          >
            {editingBug ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default DeveloperBugsPage;
